import { useCallback, useMemo, useRef } from 'react';
import { TmpCookiesObj } from 'cookies-next/lib/types';

import getSsrCookieStorage, {
  SsrStorage,
} from '@internal/services/ssr-cookie-storage';

import { mergeObjects } from '../utils/merge-objects';

export type StorageSchema = Record<string, unknown>;

export type Storage<Schema extends StorageSchema = StorageSchema> = {
  getValue: <Key extends keyof Schema, T = Schema[Key]>(key: Key) => T | null;
  setValue: <Key extends keyof Schema>(
    key: Key,
    value: Schema[Key],
    expiringDate?: Date
  ) => void;
  patchValue: <Key extends keyof Schema>(
    key: Key,
    data: Partial<Schema[Key]>,
    expiringDate?: Date
  ) => void;
  removeValue: (key: keyof Schema) => void;
  clear: () => void;
  subscribe: <Key extends keyof Schema>(
    listener: (key: Key, value: Schema[Key]) => void
  ) => () => void;
};

type StorageType = 'session' | 'local';

type ValueWithExpiringDate<T> = {
  value: T;
  expiringDate: string;
};

const hasExpiringDate = <T>(
  value: T | ValueWithExpiringDate<T>
): value is ValueWithExpiringDate<T> => {
  return (value as ValueWithExpiringDate<T>).expiringDate !== undefined;
};

type UseStorageParams<
  Schema extends StorageSchema,
  WithSSR extends boolean = false
> = {
  domainKey: string;
  type?: StorageType;
  lifetime?: number;
  onInit?: (storage: Storage<Schema>) => void;
  ssr?: {
    enabled: WithSSR;
    cookies?: TmpCookiesObj;
  };
};

const mapTypeToWindowKey: Record<StorageType, keyof Window> = {
  local: 'localStorage',
  session: 'sessionStorage',
};

export type IStorage<WithSSR> = [WithSSR] extends [true]
  ? SsrStorage
  : globalThis.Storage;

const isSsrStorage = (storage: unknown): storage is SsrStorage =>
  typeof (storage as SsrStorage)?.keys === 'function';

type Listener = (key: string, value: unknown) => void;

const subscribers: {
  [key: string]: Array<Listener>;
} = {};

export const useStorage = <
  Schema extends StorageSchema,
  WithSSR extends boolean = boolean
>({
  domainKey,
  type = 'session',
  lifetime,
  onInit,
  ssr,
}: UseStorageParams<Schema, WithSSR>): Storage<Schema> => {
  const storageRef = useRef<Storage<Schema>>({} as Storage<Schema>);

  const isInitialized = useRef(false);

  const ssrCookieStorage = useMemo(
    () => (ssr?.enabled ? getSsrCookieStorage(ssr.cookies) : null),
    [ssr]
  );

  const storage = useMemo<IStorage<WithSSR> | null>(() => {
    if (typeof window !== 'undefined') {
      return window[mapTypeToWindowKey[type]];
    }

    return ssr?.enabled ? getSsrCookieStorage(ssr.cookies) : null;
  }, [type, ssr]);

  storageRef.current.getValue = useCallback(
    <Key extends keyof Schema, T = Schema[Key]>(key: Key): T | null => {
      try {
        const strValue = storage?.getItem(`${domainKey}:${String(key)}`);
        if (!strValue) {
          return null;
        }

        if (ssr?.enabled) {
          const cookieValue = ssrCookieStorage?.getItem(
            `${domainKey}:${String(key)}`
          );

          // If we have enabled SSR but server is missing data on client,
          // we simply remove it from client to keep it sync with server
          if (!cookieValue) {
            storageRef.current.removeValue(key);
            return null;
          }
        }

        const value = JSON.parse(strValue) as T;
        if (!hasExpiringDate(value)) {
          return value;
        }

        if (Date.now() > new Date(value.expiringDate).valueOf()) {
          storageRef.current.removeValue(key);
          return null;
        }

        return value.value;
      } catch (error) {
        return null;
      }
    },
    [ssr?.enabled, ssrCookieStorage, storage, domainKey]
  );

  storageRef.current.setValue = useCallback(
    <Key extends keyof Schema>(
      key: Key,
      value: Schema[Key],
      expiringDate?: Date
    ): void => {
      try {
        const storageKey = `${domainKey}:${String(key)}`;
        const preparedValue =
          expiringDate || lifetime
            ? {
                value,
                expiringDate:
                  expiringDate ||
                  new Date(Date.now() + lifetime!).toISOString(),
              }
            : value;

        const strValue = JSON.stringify(preparedValue);
        if (!strValue) {
          return;
        }

        storage?.setItem(storageKey, strValue);
        if (ssr?.enabled) {
          ssrCookieStorage?.setItem(storageKey, strValue);
        }
        const listeners = subscribers[domainKey] ?? [];

        listeners.forEach(listener => listener(key as string, value));
      } catch (error) {
        /* empty */
      }
    },
    [storage, lifetime, ssr, ssrCookieStorage, domainKey]
  );

  storageRef.current.patchValue = useCallback(
    <Key extends keyof Schema>(
      key: Key,
      data: Partial<Schema[Key]>,
      expiringDate?: Date
    ): void => {
      const storedValue = storageRef.current.getValue(key);

      if (
        storedValue &&
        typeof storedValue === 'object' &&
        !Array.isArray(storedValue)
      ) {
        storageRef.current.setValue(
          key,
          mergeObjects({
            source: storedValue,
            target: data,
          }),
          expiringDate
        );
        return;
      }

      storageRef.current.setValue(key, data as Schema[Key], expiringDate);
    },
    []
  );

  storageRef.current.removeValue = useCallback(
    (key: keyof Schema): void => {
      try {
        storage?.removeItem(`${domainKey}:${String(key)}`);
        if (ssr?.enabled) {
          ssrCookieStorage?.removeItem(`${domainKey}:${String(key)}`);
        }
      } catch (error) {
        /* empty */
      }
    },
    [storage, ssr, ssrCookieStorage, domainKey]
  );

  storageRef.current.clear = useCallback(() => {
    try {
      const keys = ((isSsrStorage(storage)
        ? storage?.keys() ?? []
        : Object.keys(storage || {})) || []) as string[];

      keys
        .filter(key => key.startsWith(`${domainKey}:`))
        .forEach(key => {
          storage?.removeItem(key);

          if (ssr?.enabled) {
            ssrCookieStorage?.removeItem(key);
          }
        });
    } catch (error) {
      /* empty */
    }
  }, [storage, ssr, ssrCookieStorage, domainKey]);

  storageRef.current.subscribe = useCallback(
    <Key extends keyof Schema>(
      listener: (key: Key, value: Schema[Key]) => void
    ): (() => void) => {
      if (!subscribers[domainKey]) {
        subscribers[domainKey] = [];
      }

      subscribers[domainKey].push(listener as Listener);

      return () => {
        subscribers[domainKey] = subscribers[domainKey].filter(
          l => l !== listener
        );

        if (subscribers[domainKey].length === 0) {
          delete subscribers[domainKey];
        }
      };
    },
    [domainKey]
  );

  if (onInit && storage && !isInitialized.current) {
    onInit(storageRef.current);
    isInitialized.current = true;
  }

  return storageRef.current;
};

export default useStorage;
